import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { getDeviceType } from '@xanadu/ui-shared/utils/deviceDetection';
import Scrollable from '@xanadu/ui-shared/components/widgets/Scrollable';
import './Dropdown.scss';

export default class Dropdown extends Component {
  static get propTypes() {
    return {
      value: PropTypes.string,
      dataHook: PropTypes.string,
      disabled: PropTypes.bool,
      url: PropTypes.string,
      selectLabel: PropTypes.string,
      className: PropTypes.string,
      layout: PropTypes.oneOf(['outlined', 'standard', 'small']),
      theme: PropTypes.oneOf(['dark', 'light']).isRequired,
      onSelect: PropTypes.func.isRequired,
      onHeadClick: PropTypes.func,
      children: PropTypes.node.isRequired,
      items: PropTypes.arrayOf(Object).isRequired,
      style: PropTypes.instanceOf(Object),
      type: PropTypes.string,
      useDesktopDropdown: PropTypes.bool,
    };
  }

  static defaultProps() {
    return {
      children: []
    };
  }

  constructor(props) {
    super(props);

    const { items, value, selectLabel = 'Select' } = props;
    const { name: selectedItemName } = items.find(i => i.id === value || i.name === value) || {};

    this.state = {
      selectedItem: selectedItemName || selectLabel,
      itemsVisible: false
    };
    this.dropdown = null;
    this.handleItemSelection = this.handleItemSelection.bind(this);
    this.handleNativeItemSelection = this.handleNativeItemSelection.bind(this);
    this.handleOutClick = this.handleOutClick.bind(this);
    this.handleEscapeClick = this.handleEscapeClick.bind(this);
    this.handleLinkClick = this.handleLinkClick.bind(this);
    this.handleItemsVisibility = this.handleItemsVisibility.bind(this);
  }

  componentDidMount() {
    if (getDeviceType() === 'desktop') {
      document.addEventListener('click', this.handleOutClick);
      document.addEventListener('touchstart', this.handleOutClick);
      document.addEventListener('keydown', this.handleEscapeClick);
    }
  }

  componentWillReceiveProps({ value }) {
    const { items, value: currentValue } = this.props;

    if (value !== currentValue) {
      const { name } = items.find(i => i.id === value || i.name === value) || {};

      this.setState({
        selectedItem: name
      });
    }
  }

  componentWillUnmount() {
    if (getDeviceType() === 'desktop') {
      document.removeEventListener('click', this.handleOutClick);
      document.removeEventListener('touchstart', this.handleOutClick);
      document.removeEventListener('keydown', this.handleEscapeClick);
    }
  }

  handleOutClick(event) {
    if (this.dropdown) {
      const isClickInside = this.dropdown.contains(event.target);

      if (!isClickInside) {
        this.setState({
          itemsVisible: false
        });
      }
    }
  }

  handleEscapeClick(event) {
    const KEYCODE_ESC = 27;
    const isEscapeClicked = (event.keyCode === KEYCODE_ESC);

    if (isEscapeClicked) {
      this.setState({
        itemsVisible: false
      });
    }
  }

  handleLinkClick(e) {
    e.preventDefault();
    if (this.props.onHeadClick) {
      this.props.onHeadClick(this.props.url);
    }
  }

  handleItemsVisibility() {
    if (!this.props.disabled) {
      this.setState({
        itemsVisible: !this.state.itemsVisible
      });
    }
  }

  handleItemSelection(selectedItem) {
    this.props.onSelect(selectedItem);

    this.setState({
      selectedItem: selectedItem.name,
      itemsVisible: false
    });
  }

  handleNativeItemSelection() {
    const selectedValue = this.nativeSelect.value;
    let selectedItem;
    this.props.items.forEach((item) => {
      if (selectedValue === item.name) {
        selectedItem = item;
      }
    });

    this.props.onSelect(selectedItem);

    this.setState({
      selectedItem: selectedItem.name,
      itemsVisible: false
    });
  }

  renderItems() {
    let moreOptionsHintElement = null;
    let containerItems;
    if (this.state.itemsVisible) {
      if ((this.props.className === 'mb-dropdown--currency-id' && this.props.children.length > 3)
        || this.props.children.length > 6) {
        moreOptionsHintElement = (
          <div className="mb-dropdown__more-options-hint" />
        );
      }
      containerItems = (<CSSTransition classNames="fade-bottom" timeout={250}>
        <div className="mb-dropdown__optionsWrapper">
          <div
            className="mb-dropdown__options"
          >
            <Scrollable>
              {
                this.props.children.map(
                  child => React.cloneElement(child, {
                    onClick: this.handleItemSelection,
                    hooks: this.props.type === 'odds'
                  })
                )
              }
            </Scrollable>
          </div>
          {moreOptionsHintElement}
        </div>
      </CSSTransition>);
    } else {
      containerItems = null;
    }

    return (
      <TransitionGroup
        component="span"
      >
        {containerItems}
      </TransitionGroup>
    );
  }

  renderNativeItems() {
    const filteredItems = this.props.items
      .map(item => (
        <option
          key={item.id}
          value={item.name}
        >
          {item.name ? item.name.toUpperCase() : ''}
        </option>
      ));

    return filteredItems;
  }

  renderDropdownElement() {
    const { style = {}, useDesktopDropdown } = this.props;
    const themeClass = `mb-dropdown--${this.props.theme}`;
    const layoutClass = `mb-dropdown--${this.props.layout}`;
    let classNameToggleOptions = 'fa fa-caret-right mb-dropdown__toggle-arrow';
    let classNameHeadActive = '';
    let toggleOptionsElement = null;
    let headsArrowElement = (
      <i className="fa fa-caret-down mb-dropdown__head-arrow" aria-hidden="true" />
    );
    let headsClickCallback = this.handleItemsVisibility;

    if (this.state.itemsVisible) {
      classNameHeadActive = 'mb-dropdown__head--active';
      if (this.props.onHeadClick) {
        classNameToggleOptions += ' mb-dropdown__toggle-arrow--rotated';
      }
    }

    if (this.props.onHeadClick) {
      headsClickCallback =
        this.props.url ? this.handleLinkClick : this.handleItemsVisibility;

      headsArrowElement = null;
      toggleOptionsElement = (
        <button
          type="button"
          className="mb-dropdown__toggle-options"
          onClick={this.handleItemsVisibility}
          onKeyUp={(e) => {
            e.preventDefault();
            if (e.key === 'Enter') {
              this.handleItemsVisibility();
            }
          }}
          tabIndex="0"
        >
          <i className={classNameToggleOptions} aria-hidden="true" />
        </button>
      );
    }

    if (getDeviceType() === 'mobile' && !useDesktopDropdown) {
      return (
        <div
          className={`mb-dropdown mb-dropdown--mobile ${this.props.className} ${themeClass}`}
          style={style}
        >
          <select
            ref={(node) => { this.nativeSelect = node; }}
            onChange={this.handleNativeItemSelection}
            data-hook={this.props.dataHook}
            value={this.state.selectedItem}
          >
            {this.renderNativeItems()}
          </select>
        </div>
      );
    }

    return (
      <div
        className={`mb-dropdown ${this.props.className} ${themeClass} ${layoutClass}`}
        ref={(node) => { this.dropdown = node; }}
        style={style}
      >
        <div className="mb-dropdown__container-head">
          <a
            href={this.props.url}
            className={`mb-dropdown__head ${classNameHeadActive}`}
            onClick={headsClickCallback}
            data-hook={this.props.dataHook}
            onKeyUp={(e) => {
              e.preventDefault();
              if (e.key === 'Enter') {
                headsClickCallback();
              }
            }}
            tabIndex="0"
          >
            {this.state.selectedItem}
            {headsArrowElement}
          </a>
          {toggleOptionsElement}
        </div>
        <div className="mb-dropdown__container-options">
          {this.renderItems()}
        </div>
      </div>
    );
  }

  render() {
    return this.renderDropdownElement();
  }
}
